// =============================================================================
// R-239 P4 Task 8 — useProfileLearning. Polls the Capture proxy
// /ocr/profile-learning and derives a lifecycle + auto-advance summary across
// every field of every form. Plain React + setInterval (no react-query),
// mirroring useOcrHealthEndpoint. Idle (never fetches) without a jobTypeId.
// =============================================================================

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { api } from "@/lib/api-client";
import type { ProfileLearningField } from "@capture/shared";

const POLL_INTERVAL_MS = 15_000;

export interface ProfileLearningForm {
  formId: string;
  formName: string | null;
  fields: ProfileLearningField[];
}

interface ProfileLearningResponse {
  available: boolean;
  forms: ProfileLearningForm[];
  fetchedAt: string;
}

export interface ProfileLearningSummary {
  learning: number;
  proven: number;
  demoted: number;
  total: number;
  /** totalSkipped / (totalSkipped + totalPresented); 0 when no reads. */
  autoAdvancedPct: number;
}

const EMPTY_SUMMARY: ProfileLearningSummary = {
  learning: 0,
  proven: 0,
  demoted: 0,
  total: 0,
  autoAdvancedPct: 0,
};

function deriveSummary(forms: ProfileLearningForm[]): ProfileLearningSummary {
  let learning = 0;
  let proven = 0;
  let demoted = 0;
  let totalSkipped = 0;
  let totalPresented = 0;

  for (const form of forms) {
    for (const f of form.fields) {
      if (f.lifecycle === "learning") learning += 1;
      else if (f.lifecycle === "proven") proven += 1;
      else if (f.lifecycle === "demoted") demoted += 1;
      totalSkipped += f.skippedCount;
      totalPresented += f.presentedCount;
    }
  }

  const reads = totalSkipped + totalPresented;
  return {
    learning,
    proven,
    demoted,
    total: learning + proven + demoted,
    autoAdvancedPct: reads > 0 ? totalSkipped / reads : 0,
  };
}

export function useProfileLearning(
  jobTypeId?: string,
  formId?: string,
  withTrend = false,
): {
  forms: ProfileLearningForm[];
  available: boolean;
  fetchedAt: string | null;
  summary: ProfileLearningSummary;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [data, setData] = useState<ProfileLearningResponse | null>(null);
  // Idle until a jobTypeId arrives — don't show a spinner for the no-op state.
  const [isLoading, setIsLoading] = useState<boolean>(!!jobTypeId);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const path = useMemo(() => {
    if (!jobTypeId) return null;
    const params = new URLSearchParams({ jobTypeId });
    if (formId) params.set("formId", formId);
    if (withTrend) params.set("withTrend", "1");
    return `/ocr/profile-learning?${params.toString()}`;
  }, [jobTypeId, formId, withTrend]);

  const fetch = useCallback(async () => {
    if (!path) return;
    setError(null);
    try {
      const result = await api.get<ProfileLearningResponse>(path);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, [path]);

  useEffect(() => {
    if (!path) {
      // No jobTypeId → idle: clear any data and stop loading without a request.
      setData(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetch();
    intervalRef.current = setInterval(fetch, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [path, fetch]);

  const forms = data?.available ? data.forms : [];
  const summary = useMemo(
    () => (data?.available ? deriveSummary(forms) : EMPTY_SUMMARY),
    [data?.available, forms],
  );

  return {
    forms,
    available: data?.available ?? false,
    fetchedAt: data?.fetchedAt ?? null,
    summary,
    isLoading,
    error,
    refetch: fetch,
  };
}
